/**
 * Profile Form Handler
 * Handles profile information updates with validation and change detection
 */

(function () {
    'use strict';
    // State management
    var originalValues = {};
    var currentValues = {};
    var isDirty = false;

    // DOM Elements
    var firstName, lastName, email, bio;
    var saveBtn, discardBtn, bioCounter;
    var form;

    /**
     * Initialize profile form
     */
    function init() {
        // Get form elements
        form = document.getElementById('personalInfoForm');
        firstName = document.getElementById('firstName');
        lastName = document.getElementById('lastName');
        email = document.getElementById('email');
        bio = document.getElementById('bio');

        saveBtn = document.getElementById('savePersonalInfoBtn');
        discardBtn = document.getElementById('discardPersonalInfoBtn');
        bioCounter = document.getElementById('bioCounter');

        if (!firstName || !saveBtn) {
            return; // Not on profile page or viewing someone else's profile
        }

        // Capture original values
        captureOriginalValues();

        // Setup event listeners
        setupEventListeners();

        // Initialize bio counter
        if (bio && bioCounter) {
            window.Validators.updateBioCounter(bio, bioCounter);
        }
    }

    /**
     * Capture original field values
     */
    function captureOriginalValues() {
        originalValues = {
            firstName: firstName ? firstName.value : '',
            lastName: lastName ? lastName.value : '',
            email: email ? email.value : '',
            bio: bio ? bio.value : ''
        };
        currentValues = Object.assign({}, originalValues);
    }

    /**
     * Setup event listeners
     */
    function setupEventListeners() {
        // Input change events
        if (firstName) firstName.addEventListener('input', function () { handleInputChange('firstName', this.value); });
        if (lastName) lastName.addEventListener('input', function () { handleInputChange('lastName', this.value); });
        if (email) email.addEventListener('input', function () { handleInputChange('email', this.value); });
        if (bio) {
            bio.addEventListener('input', function () {
                handleInputChange('bio', this.value);
                window.Validators.updateBioCounter(this, bioCounter);
            });
        }

        // Blur validation
        if (email) email.addEventListener('blur', function () { validateField('email', this.value); });
        if (firstName) firstName.addEventListener('blur', function () { validateField('firstName', this.value); });
        if (lastName) lastName.addEventListener('blur', function () { validateField('lastName', this.value); });
        if (bio) bio.addEventListener('blur', function () { validateField('bio', this.value); });

        // Form Submission
        if (form) form.addEventListener('submit', handleSave);

        // Discard button
        if (discardBtn) discardBtn.addEventListener('click', handleDiscard);
    }

    /**
     * Handle input change
     */
    function handleInputChange(field, value) {
        currentValues[field] = value;
        checkDirty();
    }

    /**
     * Check if form has changes
     */
    function checkDirty() {
        var hasChanges = false;
        for (var key in originalValues) {
            if (originalValues[key] !== currentValues[key]) {
                hasChanges = true;
                break;
            }
        }

        if (hasChanges !== isDirty) {
            isDirty = hasChanges;
            toggleButtons(isDirty);
        }
    }

    /**
     * Toggle button states based on dirty state
     */
    function toggleButtons(hasChanges) {
        // Enable/disable save button
        if (saveBtn) {
            saveBtn.disabled = !hasChanges;
        }

        // Show/hide discard button
        if (discardBtn) {
            discardBtn.style.display = hasChanges ? 'inline-block' : 'none';
        }
    }

    /**
     * Validate a field
     */
    function validateField(fieldName, value) {
        var field = document.getElementById(fieldName);
        if (!field || !window.Validators) return true;

        var result;
        switch (fieldName) {
            case 'email':
                result = window.Validators.email(value);
                break;
            case 'firstName':
            case 'lastName':
                result = window.Validators.name(value);
                break;
            case 'bio':
                result = window.Validators.bio(value);
                break;
            default:
                return true;
        }

        if (!result.valid) {
            window.Validators.showError(field, result.message);
            return false;
        } else {
            window.Validators.clearError(field);
            return true;
        }
    }

    /**
     * Validate all fields
     */
    function validateAll() {
        if (!window.Validators) {
            console.error('Validators not loaded');
            if (window.Utils && window.Utils.showNotification) {
                window.Utils.showNotification('System error: Validation module not loaded. Please refresh the page.', 'error');
            } else {
                alert('System error: Validation module not loaded. Please refresh the page.');
            }
            return false;
        }

        var valid = true;
        valid = validateField('firstName', currentValues.firstName) && valid;
        valid = validateField('lastName', currentValues.lastName) && valid;
        valid = validateField('email', currentValues.email) && valid;
        valid = validateField('bio', currentValues.bio) && valid;
        return valid;
    }

    /**
     * Handle save button click / form submit
     */
    function handleSave(e) {
        if (e) e.preventDefault();
        if (!validateAll()) {
            return;
        }

        // Show loading state
        saveBtn.disabled = true;
        var originalText = saveBtn.textContent;
        saveBtn.innerHTML = '<span class="material-symbols-outlined text-sm animate-spin align-middle mr-1">refresh</span>Saving...';

        // Prepare data
        var data = {
            firstName: currentValues.firstName,
            lastName: currentValues.lastName,
            email: currentValues.email,
            bio: currentValues.bio
        };

        // Send AJAX request
        fetch('/user/updateProfile', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'same-origin',
            body: JSON.stringify(data)
        })
            .then(function (response) {
                return response.json();
            })
            .then(function (result) {
                saveBtn.disabled = false;
                saveBtn.textContent = originalText;

                if (result.success) {
                    // Update original values
                    captureOriginalValues();
                    toggleButtons(false);
                    if (window.Utils && window.Utils.showNotification) {
                        window.Utils.showNotification(result.message || 'Profile updated successfully', 'success');
                    } else {
                        alert(result.message || 'Profile updated successfully');
                    }
                } else {
                    if (window.Utils && window.Utils.showNotification) {
                        window.Utils.showNotification(result.message || 'Failed to update profile', 'error');
                    } else {
                        alert('Error: ' + (result.message || 'Failed to update profile'));
                    }
                }
            })
            .catch(function (error) {
                console.error('Error updating profile:', error);
                saveBtn.disabled = false;
                saveBtn.textContent = originalText;
                if (window.Utils && window.Utils.showNotification) {
                    window.Utils.showNotification('Network error. Please try again.', 'error');
                } else {
                    alert('Error: Network error. Please try again.');
                }
            });
    }

    /**
     * Handle discard button click
     */
    function handleDiscard() {
        // Reset fields to original values
        if (firstName) firstName.value = originalValues.firstName;
        if (lastName) lastName.value = originalValues.lastName;
        if (email) email.value = originalValues.email;
        if (bio) bio.value = originalValues.bio;

        // Update current values
        currentValues = Object.assign({}, originalValues);

        // Clear errors
        if (firstName) window.Validators.clearError(firstName);
        if (lastName) window.Validators.clearError(lastName);
        if (email) window.Validators.clearError(email);
        if (bio) window.Validators.clearError(bio);

        // Reset button states
        isDirty = false;
        toggleButtons(false);

        // Update bio counter
        if (bio && bioCounter) {
            window.Validators.updateBioCounter(bio, bioCounter);
        }

        if (window.Utils && window.Utils.showNotification) {
            window.Utils.showNotification('Changes discarded', 'success');
        }
    }

    /**
     * Show message to user
     * @deprecated Use window.Utils.showNotification instead
     */
    function showMessage(type, message) {
        if (window.Utils && window.Utils.showNotification) {
            window.Utils.showNotification(message, type);
        } else {
            alert(type === 'success' ? message : 'Error: ' + message);
        }
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

})();
